import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * What a device has sent about itself over time (`metrics` and `event`
 * frames, kept by the hub; the Engage Live Client sends them), and when the
 * hub saw it connected. Readable while the device is offline, which is the
 * point: the temperatures and the packet loss before it went quiet, and
 * which days and hours it was on at all.
 *
 * Form choice: each measurement is a continuous signal on its own scale
 * (°C, %, ms), so small multiples of single lines, not one chart with a
 * legend. Each point is a bucket (a minute up to 6 hours, three hours over 90
 * days): the solid line is its average, the faint one its highest, so a
 * short spike in a long range still shows. A gap of more than three buckets
 * is drawn as a gap: the device was off or unreachable, and must not be
 * bridged. The strip on top is the hub's own record of when it was
 * connected.
 */

interface Point { at: string; values: Record<string, number>; max?: Record<string, number> }
interface History {
  metrics: Point[];
  bucketMinutes?: number;
  presence?: { from: string; to: string | null }[];
  events: { at: string; level: string; text: string }[];
}

/** Labels for the measurements apps are known to send; anything else shows by its own key. */
const KNOWN: Record<string, { label: string; unit: string; max?: number }> = {
  cpu: { label: 'CPU', unit: '%', max: 100 },
  mem: { label: 'Memory used', unit: '%', max: 100 },
  tempCpu: { label: 'CPU temperature', unit: '°C' },
  tempGpu: { label: 'GPU temperature', unit: '°C' },
  tempDisk: { label: 'Disk temperature', unit: '°C' },
  gpu: { label: 'GPU', unit: '%', max: 100 },
  pingGatewayMs: { label: 'Ping to router', unit: 'ms' },
  pingInternetMs: { label: 'Ping to internet', unit: 'ms' },
  pingHubMs: { label: 'Ping to hub', unit: 'ms' },
  lossInternet: { label: 'Packet loss to internet', unit: '%', max: 100 },
  rxMbps: { label: 'Network in', unit: 'Mb/s' },
  txMbps: { label: 'Network out', unit: 'Mb/s' },
  diskFreePct: { label: 'System disk free', unit: '%', max: 100 },
  wifiSignal: { label: 'Wi-Fi signal', unit: '%', max: 100 },
  battery: { label: 'Battery', unit: '%', max: 100 },
};

const RANGES: [number, string][] = [[6, '6 h'], [24, '24 h'], [72, '3 days'], [168, '7 days'], [720, '30 days'], [2160, '90 days']];

const time = (ms: number, long: boolean) =>
  new Date(ms).toLocaleString(undefined, long
    ? { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
    : { weekday: 'short', hour: '2-digit', minute: '2-digit' });

export function DeviceHistory({ deviceId }: { deviceId: string }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      api.get<History>(`/devices/${encodeURIComponent(deviceId)}/history?hours=${hours}`)
        .then((d) => { if (live) { setData(d); setError(null); } })
        .catch((err) => { if (live) setError(err instanceof ApiError ? err.message : 'Could not load the history.'); });
    void load();
    const timer = setInterval(load, 60_000);
    return () => { live = false; clearInterval(timer); };
  }, [deviceId, hours]);

  if (error) return <div className="mt-4 text-xs text-ink-muted">{error}</div>;
  if (!data) return <div className="mt-4 text-xs text-ink-muted">Loading history…</div>;
  if (data.metrics.length === 0 && data.events.length === 0 && !data.presence?.length) return null;

  const order = Object.keys(KNOWN);
  const rank = (k: string) => (k in KNOWN ? order.indexOf(k) : 99);
  const keys = [...new Set(data.metrics.flatMap((m) => Object.keys(m.values)))].sort((a, b) => rank(a) - rank(b));
  const to = Date.now();
  const from = to - hours * 3_600_000;
  const long = hours > 48;
  const gapMs = Math.max(3 * 60_000, 3 * (data.bucketMinutes ?? 1) * 60_000);

  return (
    <div className="mt-4 space-y-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium text-ink-secondary">History</div>
        <div className="flex flex-wrap gap-1">
          {RANGES.map(([h, label]) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`rounded px-2 py-0.5 ${h === hours ? 'bg-raised text-ink' : 'text-ink-muted hover:text-ink'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {data.presence && <Presence presence={data.presence} from={from} to={to} long={long} />}
      {keys.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {keys.map((k) => <Line key={k} name={k} metrics={data.metrics} from={from} to={to} gapMs={gapMs} long={long} />)}
        </div>
      )}
      {long && keys.length > 0 && (
        <div className="text-[11px] text-ink-muted">
          Each point is {data.bucketMinutes! >= 60 ? `${data.bucketMinutes! / 60} hour${data.bucketMinutes === 60 ? '' : 's'}` : `${data.bucketMinutes} minutes`}:
          the line is the average, the faint line the highest. Beyond 30 days only hourly summaries are kept.
        </div>
      )}
      {data.events.length > 0 && (
        <div>
          <div className="mb-1 font-medium text-ink-secondary">Events</div>
          <div className="max-h-64 overflow-y-auto rounded border border-grid">
            <table className="w-full">
              <tbody>
                {data.events.map((e, i) => (
                  <tr key={i} className="border-b border-grid last:border-0">
                    <td className="whitespace-nowrap px-2 py-1 align-top text-ink-muted">{time(Date.parse(e.at), true)}</td>
                    <td
                      className="px-2 py-1"
                      style={{ color: e.level === 'error' ? 'var(--color-critical)' : e.level === 'warn' ? 'var(--color-warning)' : undefined }}
                    >
                      {e.text}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** The hub's record of when the device was connected, as a strip across the range. */
function Presence({ presence, from, to, long }: { presence: { from: string; to: string | null }[]; from: number; to: number; long: boolean }) {
  const [hover, setHover] = useState<string | null>(null);
  const spans = presence
    .map((p) => ({ a: Math.max(from, Date.parse(p.from)), b: Math.min(to, p.to ? Date.parse(p.to) : to) }))
    .filter((s) => s.b > s.a);
  const onlineMs = spans.reduce((t, s) => t + (s.b - s.a), 0);
  const pct = (t: number) => `${(((t - from) / (to - from)) * 100).toFixed(3)}%`;
  const hrs = (ms: number) => (ms >= 3_600_000 ? `${(ms / 3_600_000).toFixed(1)} h` : `${Math.round(ms / 60_000)} min`);
  return (
    <div>
      <div className="mb-1 flex justify-between gap-2">
        <span className="text-ink-secondary">Connected to the hub</span>
        <span className="text-ink-muted">{hover ?? `${Math.round((onlineMs / (to - from)) * 100)}% of the time · ${spans.length} session${spans.length === 1 ? '' : 's'}`}</span>
      </div>
      <div className="relative h-3 overflow-hidden rounded" style={{ background: 'var(--color-baseline)' }}>
        {spans.map((s, i) => (
          <div
            key={i}
            className="absolute inset-y-0"
            style={{ left: pct(s.a), width: `max(2px, calc(${pct(s.b)} - ${pct(s.a)}))`, background: 'var(--color-good)' }}
            onMouseEnter={() => setHover(`${time(s.a, true)} – ${s.b >= to - 60_000 ? 'now' : time(s.b, true)} (${hrs(s.b - s.a)})`)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-ink-muted">
        <span>{time(from, long)}</span>
        <span>now</span>
      </div>
    </div>
  );
}

function Line({ name, metrics, from, to, gapMs, long }: {
  name: string; metrics: Point[]; from: number; to: number; gapMs: number; long: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const known = KNOWN[name];
  const points = metrics
    .filter((m) => typeof m.values[name] === 'number')
    .map((m) => ({ t: Date.parse(m.at), v: m.values[name], hi: m.max?.[name] ?? m.values[name] }));
  if (points.length === 0) return null;

  const W = 300, H = 64;
  const lo = Math.min(0, ...points.map((p) => p.v));
  const hi = Math.max(known?.max ?? 0, ...points.map((p) => p.hi)) || 1;
  const x = (t: number) => ((t - from) / (to - from)) * W;
  const y = (v: number) => H - 2 - ((v - lo) / (hi - lo)) * (H - 4);
  const path = (pick: (p: (typeof points)[number]) => number) => {
    let d = '';
    points.forEach((p, i) => {
      const jump = i === 0 || p.t - points[i - 1].t > gapMs;
      d += `${jump ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(pick(p)).toFixed(1)}`;
    });
    return d;
  };
  const showMax = points.some((p) => p.hi !== p.v);
  const peak = points.reduce((a, b) => (b.hi > a.hi ? b : a));
  const shown = hover !== null ? points[hover] : null;
  const fmt = (v: number) => `${Number.isInteger(v) ? v : v.toFixed(1)}${known?.unit ? ` ${known.unit}` : ''}`;

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    const t = from + ((e.clientX - box.left) / box.width) * (to - from);
    let best = 0;
    for (let i = 1; i < points.length; i++) if (Math.abs(points[i].t - t) < Math.abs(points[best].t - t)) best = i;
    setHover(best);
  }

  return (
    <div className="rounded border border-grid p-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-ink-secondary">{known?.label ?? name}</span>
        <span className="text-ink">
          {shown ? fmt(shown.v) : fmt(points[points.length - 1].v)}
          {shown && shown.hi !== shown.v && <span className="text-ink-muted"> (max {fmt(shown.hi)})</span>}
          <span className="ml-1 text-[10px] text-ink-muted">{shown ? time(shown.t, long) : 'latest'}</span>
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-1 h-16 w-full"
        preserveAspectRatio="none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <line x1={0} x2={W} y1={H - 1} y2={H - 1} stroke="var(--color-baseline)" strokeWidth={1} />
        {showMax && (
          <path d={path((p) => p.hi)} fill="none" stroke="var(--color-series-1)" strokeOpacity={0.35} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        )}
        <path d={path((p) => p.v)} fill="none" stroke="var(--color-series-1)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        {shown && (
          <line x1={x(shown.t)} x2={x(shown.t)} y1={0} y2={H} stroke="var(--color-ink-muted)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      <div className="flex justify-between text-[10px] text-ink-muted">
        <span>{fmt(lo)}</span>
        <span>highest {fmt(peak.hi)}, {time(peak.t, long)}</span>
      </div>
    </div>
  );
}
